<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { formatBytes } from 'shared/helpers/FileHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { downloadFile } from '@chatwoot/utils';

import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import PdfPreview from 'dashboard/components-next/message/media/PdfPreview.vue';

const { attachments, sender, createdAt } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => attachments.value[0]);

const isDownloading = ref(false);

const url = computed(() => attachment.value?.dataUrl);

const fileName = computed(() => {
  if (url.value) {
    const filename = decodeURI(
      url.value.substring(url.value.lastIndexOf('/') + 1)
    );
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => fileName.value.split('.').pop().toLowerCase());
const isPdf = computed(() => fileType.value === 'pdf');

const senderName = computed(() => sender?.value?.name || '');

const displaySize = computed(() =>
  attachment.value?.fileSize ? formatBytes(attachment.value.fileSize) : ''
);

const uploadedAt = computed(() =>
  createdAt?.value ? dynamicTime(createdAt.value) : ''
);

const metaLine = computed(() =>
  [displaySize.value, uploadedAt.value].filter(Boolean).join(' · ')
);

const downloadAttachment = async () => {
  const { fileType: attachmentFileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: attachmentFileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="attachment">
    <div class="grid gap-3 min-w-64">
      <div class="grid grid-cols-[auto_1fr] gap-3 items-center">
        <div
          class="size-9 rounded-lg grid place-content-center bg-n-alpha-3 dark:bg-n-alpha-white shrink-0"
        >
          <FileIcon :file-type="fileType" class="size-5" />
        </div>
        <div class="space-y-0.5 overflow-hidden">
          <div v-if="senderName" class="text-n-slate-12 text-sm truncate">
            {{
              t('CONVERSATION.SHARED_ATTACHMENT.FILE', { sender: senderName })
            }}
          </div>
          <div class="truncate text-sm text-n-slate-12" :title="fileName">
            {{ fileName }}
          </div>
          <div v-if="metaLine" class="truncate text-xs text-n-slate-11">
            {{ metaLine }}
          </div>
        </div>
      </div>

      <PdfPreview v-if="isPdf && url" :src="url" height="16rem" />

      <div class="flex gap-2">
        <a
          :href="url"
          rel="noreferrer noopener nofollow"
          target="_blank"
          class="flex-1 flex items-center justify-center gap-1.5 bg-n-solid-3 px-4 py-2 rounded-lg text-sm text-center border border-n-container"
        >
          <Icon icon="i-lucide-external-link" class="size-3.5" />
          {{ t('COMPONENTS.MEDIA.OPEN_IN_NEW_TAB') }}
        </a>
        <Button
          slate
          faded
          icon="i-lucide-download"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          :aria-label="t('CONVERSATION.DOWNLOAD')"
          @click="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
</template>
